/**
 * Beloved — Personal prayer service
 *
 * Turns a chosen theme and the person's own words into a warm, broad-Christian
 * prayer. The prayer is written by the same server-side companion the rest of
 * the app uses (via the `/api/chat` proxy); the browser never chooses a model
 * and never sees any technical detail.
 *
 * The guiding rule is that the person should barely notice a failure: on ANY
 * problem — network, server, empty reply — this quietly returns a hand-written
 * prayer from the matching theme instead. Callers never need a try/catch.
 */

import { THEMED_PRAYERS } from '../data/prayers';
import { getPrayerAccessIdentity } from './entitlements';

/** The prayer proxy endpoint. The server decides the model. */
import { belovedApiUrl } from './apiEndpoint';

const API_URL = belovedApiUrl('/api/chat');

/** How long to wait before giving up and using a local prayer. */
const REQUEST_TIMEOUT_MS = 30000;

/** Theme used when an unknown theme id is passed in. */
const DEFAULT_THEME = 'guidance';

/* Prompt instructions now live only on the server in api/chat.js.
- speaks directly to God in a warm, reverent, comforting tone
- is broadly Christian and welcoming to any Christian tradition
- uses plain, everyday English that anyone can follow
- never encourages harm, isolation, secrecy, or relying on prayer instead of urgent human help
- if the person appears to be in danger or considering harm, affirms their worth and safety and gently asks God to bring trusted human help near
- is between 80 and 140 words
- has no title, no headings, no labels, and no markdown — only the words of the prayer
- closes with the single word "Amen." on its own.
*/

/**
 * Resolve a theme id to a known theme, falling back to guidance.
 * @param {string} themeId
 * @returns {string} a valid theme id
 */
function resolveTheme(themeId) {
  return THEMED_PRAYERS[themeId] ? themeId : DEFAULT_THEME;
}

/**
 * Build the request sent to the companion from the theme and the person's own
 * words. The theme and any words the person wrote are always included.
 *
 * @param {string} themeId the chosen theme id
 * @param {string} [userText] the person's own words (optional)
 * @returns {string} the prayer request message
 */
export function buildPrayerPrompt(themeId, userText = '') {
  const theme = THEMED_PRAYERS[resolveTheme(themeId)];
  const words = String(userText || '').trim();
  let message = `Please write a prayer about ${theme.label}.`;
  if (words) {
    message += ` In their own words, here is what is happening for them: "${words}". Gently weave their situation into the prayer.`;
  } else {
    message += ` They have not written any details, so keep the prayer warm and comforting in a general way.`;
  }
  return message;
}

/**
 * Tidy the companion's reply into a clean prayer: trim whitespace, drop any
 * surrounding quotation marks, and remove stray markdown characters. Never
 * throws — always returns a (possibly empty) string.
 *
 * @param {string} raw the raw reply text
 * @returns {string} the cleaned prayer text
 */
export function cleanPrayerText(raw) {
  return String(raw || '')
    .trim()
    .replace(/^["'“”\s]+|["'“”\s]+$/g, '')
    .replace(/[*#`_]+/g, '')
    .trim();
}

/**
 * Get a locally-written prayer for a theme. Used whenever the companion is not
 * available, so the person always receives a real prayer.
 *
 * @param {string} themeId the chosen theme id
 * @returns {string} the body of a prayer belonging to the theme
 */
export function getFallbackPrayer(themeId) {
  const theme = THEMED_PRAYERS[resolveTheme(themeId)];
  const { prayers } = theme;
  const pick = prayers[Math.floor(Math.random() * prayers.length)];
  return pick.body;
}

/**
 * Create a personal prayer for a theme and the person's words.
 *
 * Always resolves — never rejects. On success returns the companion's prayer
 * (`source: 'ai'`); on any failure returns a local themed prayer
 * (`source: 'local'`) so the experience is seamless.
 *
 * @param {string} themeId the chosen theme id
 * @param {string} [userText] the person's own words (optional)
 * @returns {Promise<{text: string, source: 'ai'|'local', reason?: string}>}
 */
export async function createPrayer(themeId, userText = '') {
  const theme = resolveTheme(themeId);

  try {
    const userMessage = buildPrayerPrompt(theme, userText);
    const accessIdentity = await getPrayerAccessIdentity();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    let response;
    try {
      response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'theme_prayer',
          input: userMessage,
          ...accessIdentity,
        }),
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response || !response.ok) {
      let errorCode = 'unavailable';
      try {
        const errorBody = await response.json();
        if (errorBody && typeof errorBody.error === 'string') {
          errorCode = errorBody.error;
        }
      } catch (e) {
        // A non-JSON error remains a generic unavailable response.
      }
      const requestError = new Error('prayer service unavailable');
      requestError.code = errorCode;
      throw requestError;
    }

    const data = await response.json();
    const replyText = data && data.content && data.content[0] && data.content[0].text;
    const cleaned = cleanPrayerText(replyText);

    if (!cleaned) {
      throw new Error('empty prayer');
    }

    return { text: cleaned, source: 'ai' };
  } catch (error) {
    // Logged for developers; the person only ever sees a real prayer.
    console.error('[Beloved] Falling back to a local prayer:', error.message);
    const reason =
      error && (error.code === 'fair_use_pause' || error.code === 'budget_pause')
        ? 'fair_use'
        : error && error.code === 'verification_unavailable'
          ? 'verification'
          : 'unavailable';
    return { text: getFallbackPrayer(theme), source: 'local', reason };
  }
}

export default createPrayer;
